import { HyperlinkButton } from 'common-components/buttons';
import _ from 'lodash';
import { Col, Icon, Row } from 'antd';
import React, { useCallback, useState } from 'react';
import CommonUtils from 'utilities/common-utils';
import Utils from 'utilities/Utils';
import { IRootDispatch } from 'stores/rematch/root-store';
import { useDispatch } from 'react-redux';
import { FilterType, TeamStatus } from 'utilities/enum-utils';
import { IFilter } from 'interfaces/filter-interfaces';
import { Tooltip2 } from '@blueprintjs/popover2';
import { ActionIcon, Button, Center, Flex, Loader, TextInput, Text } from '@good/ui/core';
import { Cross, Search } from '@good/icons';
import { useInitialiseFilterMenuData } from './use-initialise-filter-menu-data';
import { useSearchedItemList } from './use-searched-item-list';

export type IFilterMenuSearchLookupProps = {
  filter: IFilter;
  canRemove?: boolean;
  saveNewFilterValue: (filterType: string, newFilterValue: string, selectionLabel: string) => void;
  removeFilter: (filterType: string) => void;
  onClose: () => void;
};

const FilterMenuSearchLookup = ({
  filter,
  canRemove,
  saveNewFilterValue,
  removeFilter,
  onClose,
}: IFilterMenuSearchLookupProps) => {
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [localItemList, setLocalItemList] = useState([]);
  const [localSelectedItemList, setLocalSelectedItemList] = useState(filter ? _.clone(filter.values) : []);
  const [searchedItemList, setSearchedItemList] = useSearchedItemList(filter);
  const [searchValue, setSearchValue] = useState<string>('');

  const onChangeIsSearching = useCallback((searching: boolean) => {
    setIsSearching(searching);
  }, []);

  useInitialiseFilterMenuData(filter, onChangeIsSearching);

  const {
    customersStore: {
      doGetCustomersLite,
      setCustomerFilterLite,
      setCustomersLite,
      setSupportCoordinators,
      setSupportCoordinatorFilter,
      doFetchSupportCoordinators,
      setCaseManagerFilter,
      setCaseManagers,
      doFetchCaseManagers,
    },
    teamStore: { doFetchWorkerList, setWorkerFilter, setWorkerList },
    billingsStore: { doFetchBatchAuthors, setBatchAuthorsFilter, setBatchAuthors },
    groupServiceStore: { doFetchSessions, setSessions },
    servicesStore: { doFetchGroupServiceSchedules, setGroupServiceSchedules },
    bookingsStore: { doFetchSuburbs, setSuburbs },
    accountStore: { doFetchDebtorList, setDebtors },
  } = useDispatch<IRootDispatch>();

  const addSelection = (item) => {
    const newFilter = _.clone(localSelectedItemList);
    newFilter.push(item);
    setLocalSelectedItemList([...newFilter]);
  };

  const closePopover = () => {
    setIsSearching(false);
    setLocalSelectedItemList(filter ? _.clone(filter.values) : []);
    onClose();
  };

  const searchText = async (txt: string) => {
    setIsSearching(true);
    if (filter.filter === FilterType.CUSTOMER) {
      await getCustomerList(txt);
    } else if (
      filter.filter === FilterType.WORKER ||
      filter.filter === FilterType.PREFERRED_SUPPORT_WORKER ||
      filter.filter === FilterType.BLOCKED_SUPPORT_WORKER
    ) {
      await getWorkerList(txt);
    } else if (filter.filter === FilterType.BATCH_AUTHOR) {
      await getBatchAuthorList(txt);
    } else if (filter.filter === FilterType.SERVICE_DATE_TIMES) {
      await getServiceDateTimeList(txt);
    } else if (filter.filter === FilterType.GROUP_SERVICE_SCHEDULES) {
      await getGroupServiceSchedule(txt);
    } else if (filter.filter === FilterType.LOCATION_BY_SUBURBS) {
      await getSuburbs(txt);
    } else if (filter.filter === FilterType.SUPPORT_COORDINATOR) {
      await getSupportCoordinators(txt);
    } else if (filter.filter === FilterType.CASE_MANAGER) {
      await getCaseManagers(txt);
    } else if (filter.filter === FilterType.DEBTOR) {
      await getDebtors(txt);
    }
    setIsSearching(false);
  };

  const debounceSearch = _.debounce(searchText, 500);

  const onEnterSearchText = (value: string) => {
    const searchLength = _.trim(value).length;
    setSearchValue(value);
    if (searchLength >= 1) {
      setIsSearching(true);
      debounceSearch(value);
    } else if (searchLength === 0) {
      setLocalItemList([]);
      setSearchedItemList([]);
    }
  };

  const applySelection = async () => {
    saveNewFilterValue(
      filter.filter,
      localSelectedItemList,
      CommonUtils.getFilterText(filter.filter, localSelectedItemList, localItemList),
    );
    closePopover();
  };

  const removeSelection = (value) => {
    setLocalSelectedItemList(_.filter(localSelectedItemList, (item) => item.value !== value));
  };

  const getCustomerList = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setCustomerFilterLite({});
      setCustomersLite([]);
      setSearchedItemList([]);
      return;
    }
    setCustomerFilterLite({ search: searchTxt });
    await doGetCustomersLite({ sortByRelevance: true });
  };

  const getWorkerList = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setWorkerFilter({});
      setWorkerList([]);
      setSearchedItemList([]);
      return;
    }
    setWorkerFilter([
      {
        key: 'search',
        value: searchTxt,
      },
      {
        key: 'supportWorkerStatus',
        value: [TeamStatus.ENABLED, TeamStatus.DRAFT, TeamStatus.BLOCKED],
      },
    ]);
    await doFetchWorkerList({ sortByRelevance: true });
  };

  const getBatchAuthorList = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setBatchAuthorsFilter({});
      setBatchAuthors([]);
      setSearchedItemList([]);
      return;
    }
    setBatchAuthorsFilter([{ key: 'search', value: searchTxt }]);
    await doFetchBatchAuthors({});
  };

  const getServiceDateTimeList = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setSessions([]);
      setSearchedItemList([]);
      return;
    }
    await doFetchSessions({ search: searchTxt, page: 1, pageSize: 100, pageTimestamp: new Date() });
  };

  const getGroupServiceSchedule = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setGroupServiceSchedules([]);
      setSearchedItemList([]);
      return;
    }
    await doFetchGroupServiceSchedules({ search: searchTxt, page: 1, pageSize: 100, pageTimestamp: new Date() });
  };

  const getSuburbs = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setSuburbs([]);
      setSearchedItemList([]);
      return;
    }
    await doFetchSuburbs({ searchString: searchTxt });
  };

  const getSupportCoordinators = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setSupportCoordinatorFilter(null);
      setSupportCoordinators([]);
      setSearchedItemList([]);
      return;
    }
    setSupportCoordinatorFilter({ search: searchTxt });
    await doFetchSupportCoordinators({});
  };

  const getCaseManagers = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setCaseManagerFilter(null);
      setCaseManagers([]);
      setSearchedItemList([]);
      return;
    }
    setCaseManagerFilter(searchTxt);

    await doFetchCaseManagers({});
  };

  const getDebtors = async (searchTxt: string) => {
    if (Utils.isEmpty(searchTxt)) {
      setDebtors([]);
      setSearchedItemList([]);
      return;
    }
    await doFetchDebtorList({ search: searchTxt });
  };

  const filterSettings = CommonUtils.getFilterSettings(filter.filter);

  const filteredSearchedItemList = _.filter(
    searchedItemList,
    (filter) => !_.find(localSelectedItemList, (item) => filter.value === item.value),
  );

  return (
    <div aria-label='filter-search-menu' style={{ width: '800px' }}>
      <div className={'pv-small ph-medium'}>
        <Text size='lg' fw={600}>
          {filterSettings.title}
        </Text>
      </div>
      <Row>
        <Col span={12}>
          <div className='ph-medium pt-small bordered border-standard-gray' style={{ height: '42px' }}>
            <Text>
              Filtered {filterSettings.title} ({localSelectedItemList.length})
            </Text>
          </div>
          <div
            style={{ height: '200px', overflow: 'auto' }}
            className={' bordered-bottom bordered-right border-standard-gray'}
          >
            {localSelectedItemList && localSelectedItemList.length > 0 ? (
              _.map(localSelectedItemList, (item, key) => (
                <div
                  className={`flex-row justify-between pv-x-small ph-medium align-center ${
                    (filter.filter === FilterType.GROUP_SERVICE_SCHEDULES ||
                      filter.filter === FilterType.SERVICE_DATE_TIMES) &&
                    'bordered-bottom border-color-quaternary'
                  }`}
                  key={key}
                >
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '320px',
                    }}
                  >
                    {item.displayText}
                  </div>
                  {!_.isEmpty(item.tooltip) && (
                    <Tooltip2 content={item.tooltip} position={'bottom-right'}>
                      <div className={'cursor-help mh-small'}>
                        <Icon type={'info-circle'} className={'text-color-tertiary'} />
                      </div>
                    </Tooltip2>
                  )}
                  <div>
                    <HyperlinkButton
                      color={'blue-action'}
                      className={'border-blue-action'}
                      onClick={() => removeSelection(item.value)}
                    >
                      <Icon type={'close'} />
                    </HyperlinkButton>
                  </div>
                </div>
              ))
            ) : (
              <Center w='100%' h='100%'>
                <div className={'pv-large text-align-center'}>
                  <Text c='dimmed'>
                    No {filterSettings.title} selected.
                    <br /> Search and add on the right.
                  </Text>
                </div>
              </Center>
            )}
          </div>
        </Col>
        <Col span={12}>
          <Flex className='ph-small bordered-top bordered-bottom border-standard-gray' align='center' h={42}>
            {filterSettings.canSearch && (
              <TextInput
                w='100%'
                placeholder={filterSettings.searchPlaceHolder}
                onChange={(e) => onEnterSearchText(e.target.value)}
                value={searchValue}
                variant='unstyled'
                leftSection={isSearching ? <Loader size='xs' /> : <Search />}
                rightSection={
                  searchValue.trim().length > 0 ? (
                    <ActionIcon onClick={() => onEnterSearchText('')} variant='transparent' c='dimmed'>
                      <Cross />
                    </ActionIcon>
                  ) : null
                }
                size='sm'
                autoFocus={true}
              />
            )}
          </Flex>
          <div
            style={{ height: '200px', overflow: 'auto' }}
            className={' bordered-bottom bordered-right border-standard-gray'}
          >
            {isSearching ? (
              <Center w='100%' h='100%'>
                <Loader size={32} type='bars' color='dimmed' />
              </Center>
            ) : filteredSearchedItemList && filteredSearchedItemList.length > 0 ? (
              filteredSearchedItemList.map((item, key) => (
                <div
                  className={`flex-row justify-between pv-x-small ph-medium hover-bg-secondary cursor-pointer align-center ${
                    (filter.filter === FilterType.GROUP_SERVICE_SCHEDULES ||
                      filter.filter === FilterType.SERVICE_DATE_TIMES) &&
                    'bordered-bottom border-color-quaternary'
                  }`}
                  key={key}
                  onClick={() => addSelection(item)}
                >
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '320px',
                    }}
                  >
                    <Text>{item.displayText}</Text>
                  </div>
                  {!_.isEmpty(item.tooltip) && (
                    <Tooltip2 content={item.tooltip} position={'bottom-right'}>
                      <div className={'cursor-help mh-small'}>
                        <Icon type={'info-circle'} className={'text-color-tertiary'} />
                      </div>
                    </Tooltip2>
                  )}
                  <div>
                    <HyperlinkButton color={'blue-action'} className={'border-blue-action'}>
                      Add
                    </HyperlinkButton>
                  </div>
                </div>
              ))
            ) : searchedItemList && searchedItemList.length === 0 ? (
              <Center w='100%' h='100%'>
                <Text c='dimmed'>No result found</Text>
              </Center>
            ) : (
              <></>
            )}
          </div>
        </Col>
      </Row>
      <div className='p-medium'>
        <div className={'flex-row justify-between'}>
          <div>
            {canRemove && (
              <Button variant='transparent' color={'red'} onClick={() => removeFilter(filter.filter)}>
                Remove filter
              </Button>
            )}
          </div>
          <div>
            <Button variant='outline' className={'mr-medium bp3-popover-dismiss'} onClick={closePopover}>
              Cancel
            </Button>
            <Button className={'bp3-popover-dismiss border-blue-action'} onClick={applySelection}>
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterMenuSearchLookup;
